import React, { Dispatch, useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, Slide } from 'react-toastify';
import { Router } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.min.css';

import { AppPageRoutes } from 'services/routes';
// Remote meaning it is provided by the library/package in yarn, not a locally maintained file
import { DesignSystemProvider as RemoteDesignSystemProvider, Themes } from 'component-library-and-tools';
import { ThemeProvider as PrismaThemeProvider } from 'lib/prisma-theme-provider';
import { colors } from 'lib/prisma-styles';
import { AppWithNavBar } from 'services/nav';
import { getThemeMode } from 'services/user/getters';
import { LIGHT_THEME } from 'shared/constants';

import MyErrorBoundary from './services/app/MyErrorBoundary';
import AuthenticationWrapper from './pages/auth/AuthenticationWrapper';
import customHistory from "./customHistory";
import { fetchEnclaves } from './services/enclave/actionCreators';
import { arbiterMessageReceived } from './services/arbiter/actions';
import { ToggleFeature, flags, treatments } from './services/features';

// To fix a theme provider issue when running a locally linked component library, if we are running CL locally, we use the stock theme provider
const DesignSystemProvider = USING_LOCAL_COMPONENT_LIBRARY ? ThemeProvider : RemoteDesignSystemProvider;

const GlobalStyles = createGlobalStyle`
  body {
    background-color: ${colors.background.page};
    color: ${colors.content.default};
  }
`;

const StyledDesignSystemContainer = styled(DesignSystemProvider)`
    height: 100%;
`;

const NOTIFICATION_CLASS_NAME = 'workflow__notification';

const StyledToastContainer = styled(ToastContainer)`
  & {
    .${NOTIFICATION_CLASS_NAME} {
      min-height: unset;
      padding: 1rem;
      border-radius: 4px;
      font-size: 1.083333333rem;
    }
  }
`;

const App = () => {
  const dispatch = useDispatch();
  const themeMode = useSelector(getThemeMode) || localStorage.getItem('theme') || LIGHT_THEME;

  useEffect(() => {
    // Takes window message event and redux dispatcher to handle messages from arbiter
    const dispatchActionFromWindowMessage = (event: MessageEvent, d: Dispatch<any> = dispatch) => {
      d(arbiterMessageReceived(event.data));
    }

    if (CONNECTED_TO_ARBITER) {
      window.addEventListener('message', dispatchActionFromWindowMessage);
    }

    return () => {
      window.removeEventListener('message', dispatchActionFromWindowMessage);
    };
  }, []);

  useEffect(() => {
    dispatch(fetchEnclaves());
  }, []);

  return (
    // Default theme provided, there is no other active theme at the moment.
    <PrismaThemeProvider mode={themeMode}>
      <GlobalStyles />
      <StyledDesignSystemContainer theme={Themes.DefaultTheme}>
        <StyledToastContainer
          hideProgressBar
          autoClose={3000}
          position='bottom-center'
          transition={Slide}
          toastClassName={NOTIFICATION_CLASS_NAME}
        />
        <MyErrorBoundary>
          <AuthenticationWrapper>
            <ToggleFeature feature={flags.WF_APP_ENABLED} match={treatments.ON}>
              <Router history={customHistory}>
                <AppWithNavBar>
                  <AppPageRoutes />
                </AppWithNavBar>
              </Router>
            </ToggleFeature>
          </AuthenticationWrapper>
        </MyErrorBoundary>
      </StyledDesignSystemContainer>
    </PrismaThemeProvider>
  );
};

export default App;
